import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

interface SiteHeaderProps {
  overlay?: boolean;
}

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/movies", label: "伦理电影" },
  { to: "/series", label: "伦理电视剧" },
  { to: "/reviews", label: "影评专题" },
  { to: "/about", label: "关于我们" },
  { to: "/contact", label: "联系我们" },
];

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const solid = !overlay || scrolled;

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed left-0 top-0 z-50 w-full transition-all duration-300 ${
          solid
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="flex h-16 md:h-20 w-full items-center justify-between gap-4 px-4 md:px-10">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0">
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
                solid ? "bg-primary-500 text-background-50" : "bg-background-50/15 text-background-50 backdrop-blur-sm"
              }`}
            >
              <i className="ri-film-line text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-base md:text-lg font-semibold tracking-wide ${
                  solid ? "text-foreground-950" : "text-background-50"
                }`}
              >
                伦理视频
              </span>
              <span
                className={`mt-1 text-[10px] tracking-[0.25em] uppercase ${
                  solid ? "text-foreground-500" : "text-background-200"
                }`}
              >
                Ethics Cinema
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `relative whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors cursor-pointer ${
                    isActive
                      ? solid
                        ? "text-primary-600 font-medium"
                        : "text-background-50 font-medium"
                      : solid
                        ? "text-foreground-700 hover:text-primary-600"
                        : "text-background-100/85 hover:text-background-50"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={`absolute bottom-0.5 left-1/2 h-[2px] -translate-x-1/2 rounded-full bg-accent-500 transition-all duration-300 ${
                        isActive ? "w-4 opacity-100" : "w-0 opacity-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              className={`hidden md:flex items-center gap-1.5 rounded-full border pl-3 pr-1 py-1 transition-all ${
                solid
                  ? "border-background-300 bg-background-100 focus-within:border-primary-300"
                  : "border-background-50/30 bg-background-50/10 focus-within:border-background-50/60"
              }`}
            >
              <i
                className={`ri-search-line text-sm ${solid ? "text-foreground-500" : "text-background-200"}`}
              />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视作品"
                aria-label="搜索影视作品"
                className={`w-28 xl:w-36 bg-transparent text-sm outline-none ${
                  solid
                    ? "text-foreground-900 placeholder:text-foreground-400"
                    : "text-background-50 placeholder:text-background-200"
                }`}
              />
              <button
                type="submit"
                className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-500 text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
                aria-label="提交搜索"
              >
                <i className="ri-arrow-right-line text-sm" />
              </button>
            </form>

            <Link
              to="/search"
              aria-label="搜索"
              className={`md:hidden flex h-9 w-9 items-center justify-center rounded-md cursor-pointer ${
                solid ? "text-foreground-800" : "text-background-50"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="打开菜单"
              className={`lg:hidden flex h-9 w-9 items-center justify-center rounded-md cursor-pointer ${
                solid ? "text-foreground-900 hover:bg-background-200" : "text-background-50 hover:bg-background-50/15"
              }`}
            >
              <i className="ri-menu-3-line text-xl" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭菜单"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-primary-950/50 backdrop-blur-sm cursor-default"
          />
          <div className="absolute right-0 top-0 h-full w-[82%] max-w-sm overflow-y-auto bg-background-50 px-5 pb-10 pt-5 shadow-xl">
            <div className="flex items-center justify-between">
              <span className="font-heading text-lg font-semibold text-foreground-950">伦理视频</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="关闭菜单"
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <form onSubmit={handleSearch} className="mt-6 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视作品"
                aria-label="搜索影视作品"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </form>

            <nav className="mt-6 flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center justify-between border-b border-background-200 py-3.5 text-sm cursor-pointer ${
                      isActive ? "text-primary-600 font-medium" : "text-foreground-800"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </NavLink>
              ))}
            </nav>

            <div className="mt-8 rounded-lg bg-primary-500 p-4 text-background-50">
              <p className="font-heading text-sm">深度解读伦理影视</p>
              <p className="mt-1.5 text-xs leading-relaxed text-background-200">
                剧情、角色、影评三重维度，帮你看懂每一部作品的选择与代价。
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}